import React from 'react';
import { ExternalLink, Plus, X } from 'lucide-react';
import { useEditableSettings } from '@/hooks/useEditableSettings';
import { EditableText } from '@/components/EditableText';
import { SiteNav } from '@/components/SiteNav';

const Featured = () => {
  const { settings, isAdmin, queueSave, updateField, updateLabel, setUser } = useEditableSettings();

  if (!settings) {
    return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="animate-pulse text-[11px] tracking-[0.3em] uppercase text-[#525252]">Loading</div></div>;
  }

  const L = settings.labels || {};
  const links = settings.featured_links || [];
  const extractYear = (d) => {
    if (!d) return -Infinity;
    const m = String(d).match(/\d{4}/);
    return m ? parseInt(m[0], 10) : -Infinity;
  };
  // Decorate with original index so admin edits target the right entry after sort
  const sortedLinks = links.map((l, i) => ({ ...l, _i: i })).sort((a, b) => extractYear(b.date) - extractYear(a.date));

  const updateLink = (idx, field, value) => {
    const next = [...links];
    next[idx] = { ...next[idx], [field]: value };
    queueSave({ ...settings, featured_links: next });
  };
  const addLink = () => queueSave({ ...settings, featured_links: [...links, { publication: '', title: '', date: '', url: '' }] });
  const removeLink = (idx) => queueSave({ ...settings, featured_links: links.filter((_, i) => i !== idx) });

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="featured-page">
      <SiteNav settings={settings} isAdmin={isAdmin} setUser={setUser} updateField={updateField} updateLabel={updateLabel} active="featured" />

      <div className="px-4 sm:px-6 md:px-12 lg:px-24 py-12 sm:py-20">
        <div className="max-w-4xl mx-auto">
          {isAdmin && (
            <div className="mb-8 text-[10px] tracking-[0.25em] uppercase text-[#A3A3A3]">
              Inline editing on — changes save automatically
            </div>
          )}

          <EditableText
            as="h2"
            value={L.featured_title ?? 'Featured'}
            onChange={(v) => updateLabel('featured_title', v)}
            editable={isAdmin}
            className="text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-none font-light font-serif mb-4 sm:mb-6 block"
            testId="featured-title"
          />
          <EditableText
            value={L.featured_subtitle ?? 'Press, interviews, and editorial appearances.'}
            onChange={(v) => updateLabel('featured_subtitle', v)}
            editable={isAdmin}
            className="text-sm leading-relaxed tracking-wide text-[#525252] mb-10 sm:mb-16 font-serif italic block"
            testId="featured-subtitle"
          />

          {links.length === 0 && !isAdmin ? (
            <div className="text-center py-16 bg-white border border-[#E5E5E5]">
              <p className="text-[#525252] text-sm">{L.featured_empty || 'No featured items yet.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedLinks.map((link) => {
                const i = link._i;
                if (isAdmin) {
                  return (
                    <div
                      key={i}
                      className="block bg-white border border-[#E5E5E5] hover:border-[#A3A3A3] p-6 sm:p-7 group relative"
                      data-testid={`featured-link-${i}`}
                    >
                      <button
                        onClick={() => removeLink(i)}
                        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-black transition-opacity"
                        title="Remove"
                        data-testid={`remove-featured-${i}`}
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-3">
                          <EditableText
                            value={link.publication}
                            onChange={(v) => updateLink(i, 'publication', v)}
                            editable={true}
                            placeholder="Publication"
                            className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#525252] flex-1"
                            testId={`featured-publication-${i}`}
                          />
                          <EditableText
                            value={link.date}
                            onChange={(v) => updateLink(i, 'date', v)}
                            editable={true}
                            placeholder="Year"
                            className="text-[11px] tracking-[0.2em] uppercase text-[#A3A3A3] max-w-[100px]"
                            testId={`featured-date-${i}`}
                          />
                        </div>
                        <EditableText
                          value={link.title}
                          onChange={(v) => updateLink(i, 'title', v)}
                          editable={true}
                          placeholder="Article title"
                          className="text-lg sm:text-xl tracking-normal font-light font-serif block"
                          testId={`featured-title-${i}`}
                        />
                        <div className="flex items-center gap-2 pt-1">
                          <ExternalLink className="w-3.5 h-3.5 text-[#A3A3A3] shrink-0" strokeWidth={1.75} />
                          <EditableText
                            value={link.url}
                            onChange={(v) => updateLink(i, 'url', v)}
                            editable={true}
                            placeholder="https://link.com"
                            className="text-[11px] text-[#525252] flex-1"
                            testId={`featured-url-${i}`}
                          />
                          {link.url && (
                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-[#525252] hover:text-black" title="Open">
                              <ExternalLink className="w-3 h-3" strokeWidth={2} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white border border-[#E5E5E5] p-6 sm:p-7 hover:border-black transition-colors duration-300 group"
                    data-testid={`featured-link-${i}`}
                  >
                    <div className="flex items-baseline gap-3 mb-1.5">
                      <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#525252]">{link.publication}</p>
                      {link.date && <span className="text-[11px] tracking-[0.2em] uppercase text-[#A3A3A3]">{link.date}</span>}
                    </div>
                    <h3 className="text-lg sm:text-xl tracking-normal font-light font-serif mb-2 group-hover:text-black">
                      {link.title}
                    </h3>
                    <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#525252] group-hover:text-black flex items-center gap-1.5">
                      {L.featured_visit_btn || 'Read'}
                      <ExternalLink className="w-3 h-3" strokeWidth={2} />
                    </p>
                  </a>
                );
              })}
              {isAdmin && (
                <button
                  onClick={addLink}
                  className="w-full border border-dashed border-[#E5E5E5] hover:border-black p-6 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#A3A3A3] hover:text-black flex items-center justify-center gap-2"
                  data-testid="add-featured"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> Add Feature
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Featured;
